import {
  Component,
  Output,
  EventEmitter,
  Input,
  ViewEncapsulation,
  computed,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuthSessionService } from '@core/auth/auth-session.service';
import { rolesIncludeSuperadmin } from '@core/auth/superadmin-access';
import { SignOutService } from '@core/auth/sign-out.service';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import { OrderNotificationsService } from '@core/notifications/order-notifications.service';
import { formatOrderNotificationTime, OrderNotificationKind } from '@core/notifications/order-notification.model';
import { TenantContextService } from '@core/tenant/tenant-context.service';
import { initialsFromSessionUser, primaryRoleLabel as formatPrimaryRole } from '@core/auth/user-display.utils';
import { BrandingComponent } from '../sidebar/branding.component';
import { PosHeaderOpsComponent } from 'src/app/features/pos/presentation/components/pos-header-ops/pos-header-ops.component';
import { PosHeaderSearchComponent } from 'src/app/features/pos/presentation/components/pos-header-search/pos-header-search.component';
import { isPosProductCatalogSearchUrl } from 'src/app/features/pos/utils/pos-product-catalog-search-url.util';
import { CompanySwitcherComponent } from '@core/shell/company-switcher/company-switcher.component';
import { LocationSwitcherComponent } from '@core/shell/location-switcher/location-switcher.component';
import { TenantCompanySwitchService } from '@core/tenant/tenant-company-switch.service';
import { LocationContextService } from '@core/tenant/location-context.service';
import { openWorkspacePickerAndApply } from '@core/shell/workspace-picker-dialog/open-workspace-picker.util';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { PermissionContextService } from '@core/auth/permission-context.service';
import { canAccessGemasLink } from '@core/auth/gemas-access.utils';
import { resolveProfileConfigurationLink } from '@core/auth/profile-configuration-link.utils';
import { CompanyPlanContextService } from '@core/plan/company-plan-context.service';
import { tablerNavIconForState } from '@core/shell/nav-icon-weight.utils';
import { appBuildIdentity } from '@core/build-info';
import {
  confirmEnterPosVenta,
  openPosRouteInNewTab,
  POS_VENTA_ENTRY_ROUTE,
} from '@shared/ui/confirm-open-tab-dialog/confirm-enter-pos';

import { NgScrollbarModule } from 'ngx-scrollbar';

interface OrderNotificationView {
  id: string;
  kind: OrderNotificationKind;
  entityId: number | string;
  number: number;
  time: string;
  customerName?: string;
}

interface profiledd {
  id: number;
  titleKey: string;
  icon: string;
  link?: string;
  /** Ruta actual coincide con el ítem (filled + accent). */
  active?: boolean;
  new?: boolean;
  logout?: boolean;
}

@Component({
  selector: 'app-header',
  imports: [
    RouterModule,
    NgScrollbarModule,
    TablerIconsModule,
    MaterialModule,
    BrandingComponent,
    PosHeaderOpsComponent,
    PosHeaderSearchComponent,
    CompanySwitcherComponent,
    LocationSwitcherComponent,
    TranslateModule,
  ],
  templateUrl: './header.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent {
  readonly authSession = inject(AuthSessionService);
  private readonly signOutService = inject(SignOutService);
  private readonly adminShellMode = inject(AdminShellModeService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly companySwitch = inject(TenantCompanySwitchService);
  private readonly locationContext = inject(LocationContextService);
  private readonly router = inject(Router);
  private readonly permissionContext = inject(PermissionContextService);
  private readonly companyPlanContext = inject(CompanyPlanContextService);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly orderNotifications = inject(OrderNotificationsService);

  /** Buscador de productos en Venta y en Pedidos → cuenta (misma UI de catálogo). */
  readonly showPosProductSearch = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.isPosProductCatalogUrl(this.router.url)),
      startWith(this.isPosProductCatalogUrl(this.router.url))
    ),
    { initialValue: this.isPosProductCatalogUrl(this.router.url) }
  );

  private readonly routerPath = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects.split('?')[0]),
      startWith(this.router.url.split('?')[0])
    ),
    { initialValue: this.router.url.split('?')[0] }
  );

  /** Versión de build (SemVer + SHA) para soporte — solo visible para superadmin. */
  readonly appVersionLabel = appBuildIdentity();
  readonly showAppVersion = computed(() => rolesIncludeSuperadmin(this.authSession.user()?.roles));

  readonly userInitialsDisplay = computed(() => {
    const u = this.authSession.user();
    return u ? initialsFromSessionUser(u) : '';
  });

  readonly sessionRoleBadge = computed(() => {
    const u = this.authSession.user();
    return u ? formatPrimaryRole(u) : null;
  });

  readonly showWorkspaceSwitcher = computed(() => this.companySwitch.showInTenantShell());

  /** Compañía activa en sesión (menú de perfil). */
  readonly primaryCompanyDisplay = computed(() => {
    const name = this.companySwitch.activeCompany()?.name?.trim();
    return name && name.length > 0 ? name : null;
  });

  openWorkspacePicker(): void {
    openWorkspacePickerAndApply(this.dialog, {
      companies: this.companySwitch.accessibleCompanies(),
      activeCompanyId: this.companySwitch.activeCompanyId(),
      allowCreateBusiness: true,
      switchService: this.companySwitch,
      router: this.router,
      tenantContext: this.tenantContext,
      locationContext: this.locationContext,
    });
  }

  /** Nombre visible junto al avatar en la barra superior. */
  readonly toolbarUserLabel = computed(() => {
    const n = this.authSession.user()?.name?.trim();
    return n && n.length > 0 ? n : null;
  });

  readonly showShellContext = computed(
    () =>
      rolesIncludeSuperadmin(this.authSession.user()?.roles) &&
      !this.companySwitch.showInTenantShell()
  );

  /** Header compacto: sin atajos de oficina. */
  readonly isPosShell = computed(() => this.adminShellMode.mode() === 'pos');

  /** La estación integrada conserva el chrome tenant y expone sus controles POS. */
  readonly isPosWorkspace = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.isPosWorkspaceUrl(this.router.url)),
      startWith(this.isPosWorkspaceUrl(this.router.url))
    ),
    { initialValue: this.isPosWorkspaceUrl(this.router.url) }
  );

  private isPosProductCatalogUrl(url: string): boolean {
    return isPosProductCatalogSearchUrl(url);
  }

  private isPosWorkspaceUrl(url: string): boolean {
    const path = url.split('?')[0];
    return path === '/pos' || path.startsWith('/pos/') || path === '/sales/pos' || path.startsWith('/sales/pos/');
  }

  readonly shellContextLabel = computed(() => {
    if (!this.showShellContext()) {
      return '';
    }
    if (this.adminShellMode.mode() === 'platform') {
      return this.translate.instant('shell.contextPlatform');
    }
    if (this.adminShellMode.mode() === 'pos') {
      return this.translate.instant('shell.contextPos');
    }
    const name = (this.companySwitch.activeCompany()?.name ?? '').trim();
    return this.translate.instant('shell.contextMilinco', { name: name || '—' });
  });

  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Input() sidenavCollapsed = false;
  /** Overlay móvil: drawer abierto (oculta wordmark POS para no duplicar marca). */
  @Input() mobileNavOpen = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleMobileFilterNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();

  showFiller = false;

  isCollapse: boolean = false; // Initially hidden

  /** En overlay (móvil) abre el rail POS. En desktop no se renderiza. */
  onPosBrandClick(): void {
    this.toggleMobileNav.emit();
  }

  /** Deja el POS actual abierto y lanza otra estación solo tras confirmación. */
  openPosInNewTab(): void {
    confirmEnterPosVenta(this.dialog).subscribe((confirmed) => {
      if (confirmed) {
        openPosRouteInNewTab(POS_VENTA_ENTRY_ROUTE);
      }
    });
  }

  toggleCollpase() {
    this.isCollapse = !this.isCollapse; // Toggle visibility
  }

  signOut(): void {
    this.signOutService.signOut();
  }

  /** Pedidos nuevos recibidos por WebSocket (ver `OrderNotificationsService`). */
  readonly notificationsUnreadCount = this.orderNotifications.unreadCount;

  readonly orderNotificationItems = computed<OrderNotificationView[]>(() =>
    this.orderNotifications.items().map((item) => ({
      id: item.id,
      kind: item.kind,
      entityId: item.entityId,
      number: item.number,
      time: formatOrderNotificationTime(item.receivedAt),
      customerName: item.customerName,
    }))
  );

  /** Marca como leídas al abrir el menú (el badge no debe seguir sumando de fondo). */
  onNotificationsMenuOpened(): void {
    this.orderNotifications.markAllRead();
  }

  onNotificationClick(notification: OrderNotificationView): void {
    if (notification.kind === 'service_request') {
      void this.router.navigate(['/sales/requests'], {
        queryParams: { open: String(notification.entityId) },
      });
      return;
    }
    void this.router.navigate(['/sales/orders']);
  }

  readonly profileMenuItems = computed(() => {
    this.permissionContext.loadState();
    this.companyPlanContext.loadState();
    this.companyPlanContext.summary();
    const path = this.routerPath();
    const configurationLink = resolveProfileConfigurationLink(
      this.permissionContext.can.bind(this.permissionContext),
      {
        moduleConfiguration: this.companyPlanContext.moduleConfigurationEnabled(),
        moduleUsers: this.companyPlanContext.moduleUsersEnabled(),
        rolesAdmin: this.companyPlanContext.canShowRolesAdminUi(),
      }
    );

    const items: profiledd[] = [
      {
        id: 1,
        titleKey: 'header.profile.menu.myProfile',
        icon: tablerNavIconForState('user', isProfileMenuPathActive(path, '/profile')),
        link: '/profile',
        active: isProfileMenuPathActive(path, '/profile'),
      },
      {
        id: 5,
        titleKey: 'header.profile.menu.myPlan',
        icon: tablerNavIconForState('credit-card', isProfileMenuPathActive(path, '/account/plan')),
        link: '/account/plan',
        active: isProfileMenuPathActive(path, '/account/plan'),
      },
    ];

    if (canAccessGemasLink(this.permissionContext.can.bind(this.permissionContext), {
      moduleConfiguration: this.companyPlanContext.moduleConfigurationEnabled(),
      moduleUsers: this.companyPlanContext.moduleUsersEnabled(),
      rolesAdmin: this.companyPlanContext.canShowRolesAdminUi(),
    })) {
      items.push({
        id: 3,
        titleKey: 'header.profile.menu.gemas',
        icon: tablerNavIconForState('diamond', isProfileMenuPathActive(path, '/account/gemas')),
        link: '/account/gemas',
        active: isProfileMenuPathActive(path, '/account/gemas'),
      });
      items.push({
        id: 4,
        titleKey: 'header.profile.menu.academia',
        icon: tablerNavIconForState('book', isProfileMenuPathActive(path, '/account/academia')),
        link: '/account/academia',
        active: isProfileMenuPathActive(path, '/account/academia'),
      });
    }

    if (configurationLink) {
      items.push({
        id: 2,
        titleKey: 'header.profile.menu.configuration',
        icon: tablerNavIconForState(
          'settings',
          isProfileMenuPathActive(path, configurationLink)
        ),
        link: configurationLink,
        active: isProfileMenuPathActive(path, configurationLink),
      });
    }

    return items;
  });
}

function isProfileMenuPathActive(path: string, link: string): boolean {
  return path === link || path.startsWith(`${link}/`);
}
